/*
  IW Nuevo: product details as tabs (from 990px) or accordions (below). Phase 2.2, T17.
  Used by sections/iw-pdp-details.liquid, which renders ONE list of <details> plus a tab bar.

  Below 990px nothing happens: the <details> work on their own and the tab bar is hidden by CSS.
  From 990px the element switches to the WAI-ARIA tabs pattern:
    - every <details> is forced open and its summary is hidden by CSS;
    - only the selected panel is visible, the others get `hidden`;
    - panels get role="tabpanel", aria-labelledby and tabindex="0" (so Tab moves from the tab into the panel);
    - tabs use a roving tabindex, and Left / Right / Home / End move the selection (automatic activation).
  Going back below 990px removes those attributes and opens only the item that was selected.
*/
class IwDetailsTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.items = Array.from(this.querySelectorAll('.iw-details__item'));
    this.panels = this.items.map((item) => item.querySelector('.iw-details__panel'));
    if (!this.tabs.length || this.tabs.length !== this.panels.length) return;

    this.selected = Math.max(0, this.items.findIndex((item) => item.open));
    // Bind the tab handlers once: a detached and re-attached element runs connectedCallback again.
    if (!this.tabsBound) {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index, true));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });
      this.tabsBound = true;
    }

    this.query = window.matchMedia('(min-width: 990px)');
    this.onQueryChange = () => this.sync();
    // MediaQueryList.addEventListener does not exist before Safari 14.
    if (this.query.addEventListener) this.query.addEventListener('change', this.onQueryChange);
    else this.query.addListener(this.onQueryChange);

    this.tabsMode = false;
    this.sync();
  }

  disconnectedCallback() {
    if (!this.query) return;
    if (this.query.removeEventListener) this.query.removeEventListener('change', this.onQueryChange);
    else this.query.removeListener(this.onQueryChange);
  }

  sync() {
    if (this.query.matches && !this.tabsMode) this.enterTabs();
    else if (!this.query.matches && this.tabsMode) this.leaveTabs();
  }

  enterTabs() {
    this.tabsMode = true;
    // The item the visitor had open on the accordion is the one that opens as the selected tab.
    const open = this.items.findIndex((item) => item.open);
    this.selected = open === -1 ? this.selected : open;
    this.items.forEach((item) => {
      item.open = true;
    });
    this.panels.forEach((panel, index) => {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', this.tabs[index].id);
      panel.tabIndex = 0;
    });
    this.select(this.selected, false);
  }

  leaveTabs() {
    this.tabsMode = false;
    this.panels.forEach((panel) => {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-labelledby');
      panel.removeAttribute('tabindex');
      panel.hidden = false;
    });
    this.items.forEach((item, index) => {
      item.open = index === this.selected;
    });
  }

  select(index, focus) {
    this.selected = index;
    this.tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
    });
    if (this.tabsMode) {
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
    }
    if (focus) this.tabs[index].focus();
  }

  onKeydown(event, index) {
    const last = this.tabs.length - 1;
    let next;
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else return;
    event.preventDefault();
    this.select(next, true);
  }
}

if (!customElements.get('iw-details-tabs')) customElements.define('iw-details-tabs', IwDetailsTabs);
